import { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown, ChevronLeft } from "lucide-react";

/* Month dropdown — custom styled popover. Native picker takes over the
   screen on mobile and ignores styling on desktop.

   Two panes: pick a YEAR, then a month within it. A single flat list meant
   scrolling past dozens of entries to reach an older booking; billing data
   goes back to early 2026 and will keep growing.

   The year/month grid is generated here rather than read from
   BILLING_MONTHS on purpose: that constant also drives yearlyPnl in
   AppDataContext, which maps EVERY entry and would recompute a P&L row per
   month if the range were widened.

   align="left|right" anchors the popover to that edge of the trigger. */

const MONTH_NAMES = ["January","February","March","April","May","June",
                     "July","August","September","October","November","December"];
const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const POPOVER_W = 248;  // must match w-[248px] below
const POPOVER_H = 320;  // year pane + header, worst case

const FIRST_YEAR = 2020;                        // AbhiGhar, est. 2020
const LAST_YEAR = new Date().getFullYear() + 1; // one year ahead for planning

export function MonthPicker({ value, onChange, align = "left" }) {
  const [open, setOpen] = useState(false);
  const [pane, setPane] = useState("year");
  const [pickedYear, setPickedYear] = useState(null);
  const [dropUp, setDropUp] = useState(false);
  const [flipAlign, setFlipAlign] = useState(false);
  const scrollRef = useRef(null);
  const triggerRef = useRef(null);

  // Year currently represented by `value`, so the popover opens on it.
  const activeYear = (() => {
    const yr = parseInt(String(value).split(" ")[1], 10);
    return Number.isFinite(yr) ? yr : new Date().getFullYear();
  })();

  // The popover is ~300px tall and the backdrop below blocks page scrolling,
  // so one that overflows the viewport is unreachable rather than merely
  // awkward. Filter rows sit ~490px down the page, which clips on any
  // viewport under roughly 800px -- a 13" laptop with browser chrome. Flip
  // above the trigger when there isn't room below.
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();

    // Measure against <main>, not the window: AdminShell wraps every route in
    // `overflow-y-auto`, and CSS forces overflow-x to compute as auto with it
    // -- so <main> clips horizontally as well, and the sidebar means its left
    // edge is ~240px in, not 0.
    const box = triggerRef.current.closest("main")?.getBoundingClientRect()
      || { left: 0, right: window.innerWidth, bottom: window.innerHeight, top: 0 };

    setDropUp(box.bottom - r.bottom < POPOVER_H && r.top - box.top > POPOVER_H);

    // align="right" extends the popover LEFT from the trigger's right edge;
    // align="left" extends it RIGHT from the left edge. Flip when the chosen
    // side lacks room and the other has it -- on a half-width window the
    // right-aligned pickers had 222px against a 248px popover, slicing the
    // "20" off every year.
    const roomForRight = r.right - box.left;
    const roomForLeft = box.right - r.left;
    setFlipAlign(
      align === "right"
        ? roomForRight < POPOVER_W && roomForLeft >= POPOVER_W
        : roomForLeft < POPOVER_W && roomForRight >= POPOVER_W,
    );
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Reopening always starts on the year pane, scrolled to the active year.
  useEffect(() => {
    if (!open) return;
    setPane("year");
    setPickedYear(activeYear);
    const id = setTimeout(() => {
      scrollRef.current?.querySelector(`[data-year="${activeYear}"]`)
        ?.scrollIntoView({ block: "center", behavior: "auto" });
    }, 0);
    return () => clearTimeout(id);
  }, [open, activeYear]);

  const years = [];
  for (let y = LAST_YEAR; y >= FIRST_YEAR; y--) years.push(y);

  const effectiveAlign = flipAlign ? (align === "right" ? "left" : "right") : align;
  const dropdownAlign = effectiveAlign === "right" ? "right-0" : "left-0";
  const activeMonthIdx = MONTH_NAMES.indexOf(String(value).split(" ")[0]);

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 bg-white ring-1 ring-inset ring-slate-300 rounded-lg px-3 py-1.5 hover:bg-slate-50 focus:outline-none focus:ring-blue-500"
      >
        <Calendar className="w-3.5 h-3.5 text-slate-500" />
        {value}
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className={`absolute z-40 ${dropUp ? "bottom-full mb-1" : "mt-1"} ${dropdownAlign} w-[248px] max-w-[calc(100vw-1.5rem)] bg-white rounded-lg shadow-lg ring-1 ring-slate-200 overflow-hidden`}>
            {pane === "year" ? (
              <>
                <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  Select year
                </div>
                <ul ref={scrollRef} className="max-h-[min(260px,45vh)] overflow-y-auto py-1" role="listbox">
                  {years.map((y) => (
                    <li key={y} data-year={y} role="option" aria-selected={y === activeYear}>
                      <button
                        type="button"
                        onClick={() => { setPickedYear(y); setPane("month"); }}
                        className={`w-full text-left text-sm px-3 py-1.5 transition-colors tabular-nums ${
                          y === activeYear ? "bg-blue-600 text-white font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
                      >
                        {y}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setPane("year")}
                  className="w-full flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 hover:bg-slate-50 border-b border-slate-100"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span className="tabular-nums">{pickedYear}</span>
                </button>
                <div className="grid grid-cols-3 gap-1 p-2">
                  {MONTH_SHORT.map((ms, i) => {
                    const isActive = pickedYear === activeYear && i === activeMonthIdx;
                    return (
                      <button
                        key={ms}
                        type="button"
                        onClick={() => { onChange(`${MONTH_NAMES[i]} ${pickedYear}`); setOpen(false); }}
                        className={`text-sm rounded-md px-2 py-2 transition-colors ${
                          isActive ? "bg-blue-600 text-white font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
                      >
                        {ms}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
